import { useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "../../components/icons";
import { api, authHeaders, describeError } from "../../lib/api";
import type { DocumentType, DocumentTypeWrite } from "../../lib/api";
import {
  HOME_GROUPS,
  type HomeGroup,
  isSignThrough,
  toWrite,
  withSignThrough,
  useSaveDocumentType,
  type WriteError,
} from "../../lib/documentTypes";

/**
 * เครื่องมือแก้แคตตาล็อกตรง ๆ ของ super admin (`/v1/admin/document-types`)
 *
 * แยกจากหน้า catalog เพราะเป็นสิทธิ์คนละชั้นกับการ "เสนอ" ของเมือง — เมือง
 * ไม่เคยเห็น component ในไฟล์นี้ (ADR-0016)
 */

const CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,63}$/;

/** เว้นว่าง = ไม่ได้กำหนดอัตรา (fee_lak: null) ไม่ใช่ศูนย์ — คืน undefined ถ้ากรอกผิด */
function parseFee(raw: string): number | null | undefined {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

const FEE_ERROR = "ຄ່າທຳນຽມຕ້ອງເປັນຈຳນວນເຕັມ ≥ 0 ຫຼື ວ່າງໄວ້ຖ້າບໍ່ໄດ້ກຳນົດ";
const STAMP_ERROR = "ສະແຕັມອາກອນຕ້ອງເປັນຈຳນວນເຕັມ ≥ 0 (ໃສ່ 0 ຖ້າບໍ່ມີ)";

/** ສະແຕັມอากร: ต่างจากค่าธรรมเนียมตรงที่ไม่มี "ไม่ได้กำหนด" — ไม่มีคือ 0 */
function parseStamp(raw: string): number | undefined {
  const n = Number(raw.trim());
  return raw.trim() !== "" && Number.isInteger(n) && n >= 0 ? n : undefined;
}

/** ค่าที่ไม่รู้จัก (ข้อมูลเก่า) แสดงเป็น "ອື່ນໆ" เหมือนตารางและแอป — API ก็ตีค่าว่างเป็น other */
function knownGroup(g: string): HomeGroup {
  return HOME_GROUPS.find((h) => h.key === g)?.key ?? "other";
}

export function EditDocumentTypeModal({ doc, onClose }: { doc: DocumentType; onClose: () => void }) {
  const save = useSaveDocumentType();
  const [nameLo, setNameLo] = useState(doc.name_lo);
  const [nameEn, setNameEn] = useState(doc.name_en);
  const [feeLak, setFeeLak] = useState(doc.fee_lak == null ? "" : String(doc.fee_lak));
  const [stampLak, setStampLak] = useState(String(doc.stamp_lak));
  const [unitLo, setUnitLo] = useState(doc.unit_lo);
  const [homeGroup, setHomeGroup] = useState(knownGroup(doc.home_group));
  const [isExempt, setIsExempt] = useState(doc.is_exempt);
  const [isPopular, setIsPopular] = useState(doc.is_popular);
  // ADR-0031: ຂັ້ນນາຍບ້ານຢັ້ງຢືນ ໃຊ້ຈິງເມື່ອບ້ານຂອງຄຳຮ້ອງມີບັນຊີນາຍບ້ານດ້ວຍ — ບໍ່ມີ = flow ເດີມ
  const [villageHead, setVillageHead] = useState(doc.requires_village_head_signature);
  // ເຊັນຜ່ານ: ຜູ້ຂໍແນບເອກະສານຂອງຕົນເອງ ຕາແສງເຊັນລົງໄຟລ໌ນັ້ນ (ບໍ່ມີຊ່ອງກອກ)
  const initialSignThrough = isSignThrough(doc);
  const [signThrough, setSignThrough] = useState(initialSignThrough);
  const [error, setError] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!nameLo.trim()) {
      setError("ກະລຸນາໃສ່ຊື່ພາສາລາວ");
      return;
    }
    const fee = parseFee(feeLak);
    if (fee === undefined) {
      setError(FEE_ERROR);
      return;
    }
    const stamp = parseStamp(stampLak);
    if (stamp === undefined) {
      setError(STAMP_ERROR);
      return;
    }
    setError(null);
    save.mutate(
      {
        id: doc.id,
        version: doc.version,
        body: {
          ...toWrite(doc),
          name_lo: nameLo.trim(),
          name_en: nameEn.trim() || undefined,
          fee_lak: fee,
          stamp_lak: stamp,
          unit_lo: unitLo.trim() || undefined,
          home_group: homeGroup,
          is_exempt: isExempt,
          is_popular: isPopular,
          requires_village_head_signature: villageHead,
          // แตะ form_schema เฉพาะเมื่อสวิตช์เปลี่ยนจริง — ไม่งั้นฟอร์มที่ผู้ดูแลปรับไว้ถูกเขียนทับ
          ...(signThrough !== initialSignThrough ? withSignThrough(doc, signThrough) : {}),
        },
      },
      {
        onSuccess: onClose,
        onError: (err) => {
          setError(err.status === 409 ? "ຂໍ້ມູນຖືກແກ້ຈາກບ່ອນອື່ນ — ກະລຸນາປິດແລ້ວເປີດໃໝ່" : err.message);
        },
      },
    );
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={`ແກ້ໄຂ ${doc.code}`}>
      <form className="modal" onSubmit={submit}>
        <div className="modal__header">
          <h2 className="modal__title">
            ແກ້ໄຂ <span className="num">{doc.code}</span>
          </h2>
          <button type="button" className="btn-outline" onClick={onClose} aria-label="ປິດ">
            <X size={16} />
          </button>
        </div>

        <div className="modal__grid">
          <div>
            <label htmlFor="edit-name-lo" className="form-field-label">
              ຊື່ພາສາລາວ<span className="required"> *</span>
            </label>
            <input
              id="edit-name-lo"
              value={nameLo}
              onChange={(e) => {
                setNameLo(e.target.value);
              }}
              className="input-field"
            />
          </div>
          <div>
            <label htmlFor="edit-name-en" className="form-field-label">
              ຊື່ພາສາອັງກິດ
            </label>
            <input
              id="edit-name-en"
              value={nameEn}
              onChange={(e) => {
                setNameEn(e.target.value);
              }}
              className="input-field"
            />
          </div>
          <div>
            <label htmlFor="edit-fee" className="form-field-label">
              ຄ່າທຳນຽມ (ກີບ)
            </label>
            <input
              id="edit-fee"
              type="number"
              min={0}
              step={1000}
              value={feeLak}
              onChange={(e) => {
                setFeeLak(e.target.value);
              }}
              placeholder="ວ່າງໄວ້ = ບໍ່ໄດ້ກຳນົດ"
              className="input-field num"
              disabled={isExempt}
            />
          </div>
          <div>
            <label htmlFor="edit-stamp" className="form-field-label">
              ສະແຕັມອາກອນ / ສະບັບ (ກີບ)
            </label>
            <input
              id="edit-stamp"
              type="number"
              min={0}
              step={1000}
              value={stampLak}
              onChange={(e) => {
                setStampLak(e.target.value);
              }}
              placeholder="0 = ບໍ່ມີ"
              className="input-field num"
            />
          </div>
          <div>
            <label htmlFor="edit-unit" className="form-field-label">
              ຫົວໜ່ວຍ
            </label>
            <input
              id="edit-unit"
              value={unitLo}
              onChange={(e) => {
                setUnitLo(e.target.value);
              }}
              className="input-field"
              disabled={isExempt}
            />
          </div>
          <div>
            <label htmlFor="edit-group" className="form-field-label">
              ໝວດ (ກຸ່ມໃນໜ້າຫຼັກຂອງແອັບ)
            </label>
            <select
              id="edit-group"
              value={homeGroup}
              onChange={(e) => {
                setHomeGroup(knownGroup(e.target.value));
              }}
              className="input-field"
            >
              {HOME_GROUPS.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.labelLo}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="stack" style={{ marginBottom: "var(--space-6)" }}>
          <label className="row body-sm" style={{ gap: "var(--space-2)" }}>
            <input
              type="checkbox"
              checked={isExempt}
              onChange={(e) => {
                setIsExempt(e.target.checked);
              }}
            />
            ຍົກເວັ້ນຄ່າທຳນຽມ
          </label>
          <label className="row body-sm" style={{ gap: "var(--space-2)" }}>
            <input
              type="checkbox"
              checked={isPopular}
              onChange={(e) => {
                setIsPopular(e.target.checked);
              }}
            />
            ບໍລິການທີ່ໃຊ້ຫຼາຍ (ຂຶ້ນແຖວແນະນຳໜ້າຫຼັກ)
          </label>
          <div>
            <label className="row body-sm" style={{ gap: "var(--space-2)" }}>
              <input
                type="checkbox"
                checked={villageHead}
                onChange={(e) => {
                  setVillageHead(e.target.checked);
                }}
              />
              ຕ້ອງໃຫ້ນາຍບ້ານຢັ້ງຢືນ
            </label>
            <p className="caption">
              ລາຍເຊັນນາຍບ້ານຈະຂຶ້ນໃນເອກະສານທີ່ອອກ ສະເພາະປະເພດນີ້ · ຄຳຮ້ອງຈະລໍນາຍບ້ານຢັ້ງຢືນ (ບໍ່ເກີນ 2 ວັນເຮັດການ) ກ່ອນເຂົ້າຄິວຕາແສງ
              — ສະເພາະບ້ານທີ່ມີບັນຊີນາຍບ້ານ ບ້ານອື່ນເຂົ້າຄິວຕາແສງທັນທີຄືເດີມ · ມີຜົນກັບຄຳຮ້ອງທີ່ຍື່ນຫຼັງບັນທຶກ
            </p>
          </div>
          <div>
            <label className="row body-sm" style={{ gap: "var(--space-2)" }}>
              <input
                type="checkbox"
                checked={signThrough}
                onChange={(e) => {
                  setSignThrough(e.target.checked);
                }}
              />
              ເຊັນຜ່ານ (ຜູ້ຂໍແນບເອກະສານຂອງຕົນເອງ)
            </label>
            <p className="caption">
              ຜູ້ຂໍບໍ່ຕ້ອງກອກຂໍ້ມູນ — ແນບເອກະສານ (ຮູບ ຫຼື PDF) ບັງຄັບແນບກ່ອນຍື່ນ ຕາແສງກວດແລ້ວເຊັນ ລະບົບປະທັບລາຍເຊັນ ແລະ QR ລົງໃນໄຟລ໌ນັ້ນ
              {signThrough !== initialSignThrough && signThrough
                ? " · ຟອມກອກເດີມຂອງປະເພດນີ້ຈະຖືກແທນທີ່ (ບໍ່ມີຊ່ອງກອກ) ເມື່ອບັນທຶກ"
                : ""}
            </p>
          </div>
        </div>

        {error ? <div className="alert-error">{error}</div> : null}

        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={save.isPending}>
            ຍົກເລີກ
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກ"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function CreateDocumentTypeModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    code: "",
    name_lo: "",
    name_en: "",
    fee_lak: "",
    unit_lo: "",
    home_group: "other" as HomeGroup,
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation<DocumentType, WriteError, DocumentTypeWrite>({
    mutationFn: async (body) => {
      const { data, error: err, response } = await api.POST("/v1/admin/document-types", {
        body,
        headers: authHeaders(),
      });
      if (err || !data) {
        throw { status: response.status, message: describeError(err, response.status) } satisfies WriteError;
      }
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document-types"] });
      onClose();
    },
    onError: (err) => {
      setError(err.status === 409 ? "ລະຫັດນີ້ມີຢູ່ແລ້ວ — ເລືອກລະຫັດອື່ນ" : err.message);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const code = form.code.trim().toUpperCase();
    if (!CODE_PATTERN.test(code)) {
      setError("ລະຫັດຕ້ອງເປັນ A-Z, 0-9, _ ຂຶ້ນຕົ້ນດ້ວຍຕົວອັກສອນ ຍາວ 2–64 ຕົວ ເຊັ່ນ A77");
      return;
    }
    if (!form.name_lo.trim()) {
      setError("ກະລຸນາໃສ່ຊື່ພາສາລາວ");
      return;
    }
    const fee = parseFee(form.fee_lak);
    if (fee === undefined) {
      setError(FEE_ERROR);
      return;
    }
    setError(null);
    create.mutate({
      code,
      name_lo: form.name_lo.trim(),
      name_en: form.name_en.trim() || undefined,
      fee_lak: fee,
      unit_lo: form.unit_lo.trim() || undefined,
      home_group: form.home_group,
      requires_village_head_signature: false,
      is_active: true,
    });
  }

  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: { target: { value: string } }) => {
      setForm({ ...form, [key]: e.target.value });
    },
  });

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="ເພີ່ມເອກະສານ">
      <form className="modal" onSubmit={submit}>
        <div className="modal__header">
          <h2 className="modal__title">ເພີ່ມເອກະສານ</h2>
          <button type="button" className="btn-outline" onClick={onClose} aria-label="ປິດ">
            <X size={16} />
          </button>
        </div>

        <div className="modal__grid">
          <div>
            <label htmlFor="new-code" className="form-field-label">
              ລະຫັດ<span className="required"> *</span>
            </label>
            <input
              id="new-code"
              {...field("code")}
              placeholder="A77"
              className="input-field num"
              style={{ textTransform: "uppercase" }}
            />
          </div>
          <div>
            <label htmlFor="new-fee" className="form-field-label">
              ຄ່າທຳນຽມ (ກີບ)
            </label>
            <input
              id="new-fee"
              type="number"
              min={0}
              step={1000}
              {...field("fee_lak")}
              placeholder="ວ່າງໄວ້ = ບໍ່ໄດ້ກຳນົດ"
              className="input-field num"
            />
          </div>
          <div>
            <label htmlFor="new-name-lo" className="form-field-label">
              ຊື່ພາສາລາວ<span className="required"> *</span>
            </label>
            <input id="new-name-lo" {...field("name_lo")} placeholder="ໃບຢັ້ງຢືນທີ່ຢູ່" className="input-field" />
          </div>
          <div>
            <label htmlFor="new-unit" className="form-field-label">
              ຫົວໜ່ວຍ
            </label>
            <input id="new-unit" {...field("unit_lo")} placeholder="ສະບັບ" className="input-field" />
          </div>
          <div>
            <label htmlFor="new-name-en" className="form-field-label">
              ຊື່ພາສາອັງກິດ
            </label>
            <input
              id="new-name-en"
              {...field("name_en")}
              placeholder="Residence certificate"
              className="input-field"
            />
          </div>
          <div>
            <label htmlFor="new-group" className="form-field-label">
              ໝວດ (ກຸ່ມໃນໜ້າຫຼັກຂອງແອັບ)
            </label>
            <select
              id="new-group"
              value={form.home_group}
              onChange={(e) => {
                setForm({ ...form, home_group: knownGroup(e.target.value) });
              }}
              className="input-field"
            >
              {HOME_GROUPS.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.labelLo}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error ? <div className="alert-error">{error}</div> : null}

        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={create.isPending}>
            ຍົກເລີກ
          </button>
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? "ກຳລັງບັນທຶກ…" : "ເພີ່ມເອກະສານ"}
          </button>
        </div>
      </form>
    </div>
  );
}
